import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Table } from 'core-components/library/list'
import { getStaticCrud } from 'core-components/types/helpers'
import { useNavigate } from 'core/hooks/useNavigate'
import { ProgressBar, Tag } from 'partner360/components/qualityIndicator'
import { scrollToP360Tabs } from 'partner360/lib/scrollToP360Tabs'
import { appendP360TabScrollIntent } from 'partner360/lib/tabScrollIntent'

import { TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  IFinancialPlanQualityCategoryScore,
  IFinancialPlanQualityPillar,
} from '../../../../model/types'

import { getColumns, IFinancialPlanQualityTableRow } from './helpers/getColumns'

import styles from './FinancialPlanQualityPillar.module.scss'

const HIERARCHY_ROOT = 'plan-quality-root'

interface IFinancialPlanQualityPillarProps {
  title: string
  description: string
  pillar: IFinancialPlanQualityPillar
  onHowScore: () => void
  onCategoryPlanningClick?: () => void
  isPlanningOpenInNewTab?: boolean
  initialDetailsOpen?: boolean
  /** Told whether the details table stands open, whenever that changes — for a consumer that
   *  repeats the card elsewhere, such as the cockpit's export. */
  onDetailsOpenChange?: (isOpen: boolean) => void
}

export const FinancialPlanQualityPillar: FC<IFinancialPlanQualityPillarProps> = ({
  title,
  description,
  pillar,
  onHowScore,
  onCategoryPlanningClick,
  isPlanningOpenInNewTab = false,
  initialDetailsOpen = false,
  onDetailsOpenChange,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(initialDetailsOpen)

  useEffect(() => {
    onDetailsOpenChange?.(isDetailsOpen)
  }, [onDetailsOpenChange, isDetailsOpen])

  const navigate = useNavigate()
  const goToPlanning = useCallback(
    (url: string) => {
      if (isPlanningOpenInNewTab) {
        window.open(appendP360TabScrollIntent(url), '_blank', 'noopener,noreferrer')
        return
      }

      scrollToP360Tabs()
      onCategoryPlanningClick?.()
      navigate(url)
    },
    [isPlanningOpenInNewTab, navigate, onCategoryPlanningClick]
  )
  const columns = useMemo(
    () => getColumns(pillar.valueUnit, goToPlanning),
    [goToPlanning, pillar.valueUnit]
  )
  const tableRows = useMemo<IFinancialPlanQualityTableRow[]>(() => {
    const flatten = (
      categories: IFinancialPlanQualityCategoryScore[],
      parentId: string
    ): IFinancialPlanQualityTableRow[] =>
      categories.flatMap((category) => [
        {
          ...category,
          parentId,
          hasChildren: Boolean(category.children?.length),
        },
        ...flatten(category.children ?? [], category.id),
      ])

    return flatten(pillar.categories, HIERARCHY_ROOT)
  }, [pillar.categories])
  const hasHierarchy = pillar.categories.some((category) => Boolean(category.children?.length))
  const tableSource = useMemo(
    () =>
      getStaticCrud({
        items: tableRows,
        keyField: 'id',
        hierarchyField: 'parentId',
        hierarchyRoot: HIERARCHY_ROOT,
      }),
    [tableRows]
  )

  return (
    <div className={styles.pillar}>
      <div className={styles.pillarHeader}>
        <div>
          <div className={styles.pillarTitle}>
            <Translation text={title} />
          </div>

          <div className={styles.pillarDescription}>
            <Translation text={description} />
          </div>
        </div>

        <div className={styles.pillarScoreWrapper}>
          <span className={styles.pillarScore}>{pillar.score}%</span>
          <Tag variant={pillar.band} />
        </div>
      </div>

      <ProgressBar value={pillar.score} />

      <div className={styles.actions}>
        <Button variant={'outlined'} onClick={() => setIsDetailsOpen((value) => !value)}>
          <Translation
            text={
              isDetailsOpen
                ? TRANSLATION_KEYS.quality.actions.hideDetails
                : TRANSLATION_KEYS.quality.actions.showDetails
            }
          />
        </Button>

        <Button onClick={onHowScore} variant={'outlined'}>
          <Translation text={TRANSLATION_KEYS.quality.actions.howWeScore} />
        </Button>
      </div>

      {isDetailsOpen && (
        <div className={styles.detailsTable}>
          <Table source={tableSource} columns={columns} hasHierarchy={hasHierarchy} header />
        </div>
      )}
    </div>
  )
}
